import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { MarketToolsBar } from "@/components/MarketToolsBar";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, collectionSchema, howToSchema } from "@/lib/schema";
import { ARCADE_GAMES } from "@/lib/games-shared";
import { activeVariant } from "@/lib/seo-ab-titles";
import { ShareButton } from "@/components/ShareButton";

const AchievementWall = lazy(() =>
  import("@/components/games/AchievementWall").then((m) => ({ default: m.AchievementWall })),
);

const TITLE = "Free Palworld Games Online — Play 11 in Browser";
const DESCRIPTION =
  "Play 11 free Palworld games in your browser: cake baking sim, base building sim, Pal catching, TCG memory match, fusion generator, type duel and more.";
const OG_IMAGE = "https://palstars.com/og/palworld-games.jpg";
// Weekly meta-title A/B test: Search Console attributes each week's CTR to one
// variant (reported in the admin console).
const AB = activeVariant("/palworld-games");

const STEPS = [
  { name: "Pick a game", text: "Choose one of the eleven free Palworld browser games from the arcade list — no download, no install and no account needed." },
  { name: "Play a round", text: "Each game runs in a single screen: fuse two Pals, name a silhouette, roll a draft team, call a type matchup, guess a card value, vote a bracket or flip cards on the market." },
  { name: "Share your result", text: "Press the share button to copy a spoiler-free result line plus a referral link back to the game page." },
  { name: "Collect badges", text: "Clearing goals unlocks cosmetic achievement badges that are stored in your browser — they never grant packs, spins or gold." },
];

const FAQ: FaqItem[] = [
  {
    q: "What free Palworld games can I play in the browser?",
    a: `Pal Stars runs ${ARCADE_GAMES.length} free Palworld games with no download: the Pal Fusion Lab, the Who's That Pal? silhouette quiz, Pal Draft Roulette, the Type Chart Duel, the Best Pal bracket, the Card Price Guesser, the Card Market Flip trading sim, Sekhmet's cake baking simulator and Cattiva's base building simulator. Every one of them plays in a single screen on desktop and mobile.`,
  },
  {
    q: "Are the Palworld games free and download-free?",
    a: "Yes. All arcade games are free, run entirely in your browser, need no install and work without an account. A free Pal Stars account only matters for the reward games — booster packs, the Palsino and the daily Pal Puzzle.",
  },
  {
    q: "Do the arcade games give free booster packs or Palsino spins?",
    a: "No. The arcade is purely for fun and sharing: scores and badges are cosmetic and saved in your browser. Free booster packs come from the daily pack allowance, the rewards board and the login streak; Palsino spins come from the daily claim and the Pal Puzzle.",
  },
  {
    q: "How do you stop cheating on the Palworld game scores?",
    a: "Each game has a per-day play cap that resets at 00:00 UTC, a minimum time per round so a scripted run is rejected, and a hard score ceiling derived from the round count. Because no game pays account currency, there is nothing worth farming in the first place.",
  },
  {
    q: "Can I share my Palworld game result?",
    a: "Yes. Every game copies a short result line plus a referral share link to the same page, so friends who open it land on the exact game you played. Fusions also carry the two Pals in the link, so the fusion rebuilds itself.",
  },
  {
    q: "Which Palworld game is the most addictive?",
    a: "The Pal Fusion Lab has the highest share rate because every fusion is a new hybrid with its own link, while the Who's That Pal? gauntlet and the 60-second Type Chart Duel are the most replayable — both take under a minute per run.",
  },
  {
    q: "Do the Palworld browser games work on mobile?",
    a: "Yes. Every game is thumb-friendly, uses large tap targets, respects the sitewide mute and volume control, and loads its interactive code only after the page has rendered so it stays fast on mobile data.",
  },
];

export const Route = createFileRoute("/palworld-games")({
  head: () => ({
    meta: [
      { title: AB?.title ?? TITLE },
      { name: "description", content: AB?.description ?? DESCRIPTION },
      { property: "og:title", content: "Pal Stars Arcade — 11 free Palworld browser games" },
      { property: "og:description", content: "Cake baking, base building, fusion lab, silhouette quiz, type duel, card price quiz and market flip — all free, all in-browser." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/palworld-games" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:alt", content: "Pal Stars Arcade — 11 free Palworld browser games" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "robots", content: "index, follow, max-image-preview:large" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/palworld-games" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Palworld Games", "/palworld-games"]]),
      collectionSchema({
        name: "Free Palworld browser games",
        description: DESCRIPTION,
        path: "/palworld-games",
        items: ARCADE_GAMES.map((g) => ({ name: g.name, path: g.path })),
      }),
      howToSchema({
        name: "How to play the free Palworld browser games",
        description: "Pick a game, play a round in one screen, share the result link and collect cosmetic badges.",
        path: "/palworld-games",
        steps: STEPS,
      }),
      faqSchema(FAQ, "https://palstars.com/palworld-games"),
    ],
  }),
  component: GamesHub,
});

function GamesHub() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars Arcade</p>
        <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld games you can play free in your browser</h1>
          <ShareButton title={"Palworld games you can play free in your browser — Pal Stars"} text={"Free Palworld browser games on Pal Stars."} path="/palworld-games" />
        </div>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          Pal Stars runs {ARCADE_GAMES.length} free Palworld games with no download and no account: fuse two Pals into a
          hybrid, name blacked-out silhouettes, roll a random draft team, call element matchups against the clock, vote a
          16-Pal bracket, guess real card values, flip cards on a virtual market, bake cakes with Sekhmet and raise a base
          with Cattiva.
        </p>
      </header>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">All {ARCADE_GAMES.length} free Palworld games</h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {ARCADE_GAMES.map((g) => (
            <li key={g.id} className="rounded-lg border border-border/60 p-4">
              <h3 className="text-base font-semibold">
                <Link to={g.path as never} className="text-primary hover:underline">{g.name}</Link>
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">{g.blurb}</p>
              <p className="mt-2 text-xs text-muted-foreground">Fair-use cap: {g.dailyLimit} plays per UTC day</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">How to play</h2>
        <ol className="mt-3 space-y-3">
          {STEPS.map((s, i) => (
            <li key={s.name} id={`step-${i + 1}`} className="rounded-lg border border-border/60 p-3">
              <p className="text-sm font-semibold">{i + 1}. {s.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Achievement badges</h2><p className="mt-2 text-sm text-muted-foreground">Loading your badge wall…</p></section>}>
        <AchievementWall heading="Every Pal Stars arcade badge" />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Reward games and databases</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li><Link to="/weekly-tournament" className="font-semibold text-primary hover:underline">Weekly arcade tournament →</Link> three featured games every week, no account needed.</li>
          <li><Link to="/daily-puzzle" className="font-semibold text-primary hover:underline">Palworld Pal Puzzle →</Link> free daily guessing game — playable as a guest, spins need a free account.</li>
          <li><Link to="/palsino" className="font-semibold text-primary hover:underline">Pal Stars Palsino →</Link> the free Palworld casino reels.</li>
          <li><Link to="/pack-opening" className="font-semibold text-primary hover:underline">Pack opening game →</Link> open free daily booster packs.</li>
          <li><Link to="/pals" className="font-semibold text-primary hover:underline">Palworld Pal database →</Link> the stats every arcade game reads from.</li>
          <li><Link to="/elements" className="font-semibold text-primary hover:underline">Palworld elemental chart →</Link> study before the type duel.</li>
          <li><Link to="/prices" className="font-semibold text-primary hover:underline">Palworld TCG price chart →</Link> the values behind the price quiz.</li>
        </ul>
      </section>

      <MarketToolsBar />

      <FaqSection items={FAQ} heading="Free Palworld games FAQ" />
    </div>
  );
}
